import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';

const SmtpConfig = ({ isAdmin, onSave }) => {
  const [smtpConfig, setSmtpConfig] = useState({
    host: '',
    port: '',
    username: '',
    password: '',
    fromEmail: '',
  });
  const [smtpSuccess, setSmtpSuccess] = useState('');
  const [smtpError, setSmtpError] = useState('');
  const [smtpFormValid, setSmtpFormValid] = useState(false);
  const [testEmailStatus, setTestEmailStatus] = useState('');
  const [testEmailError, setTestEmailError] = useState('');
  const [isTesting, setIsTesting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Default SMTP config to avoid dependency issue
  const defaultSmtpConfig = {
    host: '',
    port: '',
    username: '',
    password: '',
    fromEmail: '',
  };

  // Fetch SMTP config from Firestore on mount
  useEffect(() => {
    if (!isAdmin) return;

    const fetchSmtpConfig = async () => {
      try {
        const smtpDocRef = doc(db, 'smtpSettings', 'config');
        const smtpDoc = await getDoc(smtpDocRef);
        if (smtpDoc.exists()) {
          setSmtpConfig(smtpDoc.data());
        } else {
          await setDoc(smtpDocRef, defaultSmtpConfig); // Use static default instead of smtpConfig
        }
      } catch (err) {
        console.error('Error loading SMTP settings:', err);
        setSmtpError('Failed to load SMTP settings: ' + err.message);
      }
    };

    fetchSmtpConfig();
  }, [isAdmin]); // Only depends on isAdmin

  // Validate form whenever smtpConfig changes
  useEffect(() => {
    const isValid = Object.values(smtpConfig).every((value) => value.trim() !== '');
    setSmtpFormValid(isValid);
  }, [smtpConfig]); // Correct dependency

  // Handle saving SMTP settings
  const handleSaveSmtpSettings = async () => {
    if (!smtpFormValid) {
      setSmtpError('Please fill out all required fields.');
      return;
    }
    setSmtpSuccess('');
    setSmtpError('');
    setIsSaving(true);
    try {
      await onSave(smtpConfig);
      setSmtpSuccess('SMTP settings saved successfully!');
    } catch (err) {
      console.error('Error saving SMTP settings:', err);
      setSmtpError('Failed to save SMTP settings: ' + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle sending test email
  const handleTestEmail = async () => {
    if (!smtpFormValid) {
      setTestEmailError('Please fill out all required fields before testing.');
      return;
    }
    setTestEmailStatus('');
    setTestEmailError('');
    setIsTesting(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/send-contact-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          to: smtpConfig.fromEmail,
          subject: 'Test Email from Medpredix AI',
          message: `This is a test email sent from Medpredix AI to verify your SMTP configuration. Sent At: ${new Date().toISOString()}`,
          smtpConfig: smtpConfig,
        }),
      });

      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        let errorMessage = `Failed to send test email (Status: ${response.status})`;
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } else {
          const text = await response.text();
          console.error('Non-JSON response from server:', text);
          errorMessage += `: ${text.slice(0, 100)}...`;
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      setTestEmailStatus(data.message || 'Test email sent successfully! Check your inbox.');
    } catch (err) {
      console.error('Error sending test email:', err);
      setTestEmailError('Failed to send test email: ' + err.message);
    } finally {
      setIsTesting(false);
    }
  };

  // Clear messages after a timeout
  useEffect(() => {
    if (smtpSuccess || smtpError || testEmailStatus || testEmailError) {
      const timer = setTimeout(() => {
        setSmtpSuccess('');
        setSmtpError('');
        setTestEmailStatus('');
        setTestEmailError('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [smtpSuccess, smtpError, testEmailStatus, testEmailError]);

  return (
    <div className="bg-white rounded-lg shadow-md p-6 border border-gray-300">
      {smtpSuccess && <div className="mb-4 p-3 bg-green-100 text-green-700 rounded-lg">{smtpSuccess}</div>}
      {smtpError && <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">{smtpError}</div>}
      {testEmailStatus && <div className="mb-4 p-3 bg-green-100 text-green-700 rounded-lg">{testEmailStatus}</div>}
      {testEmailError && <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">{testEmailError}</div>}

      <h3 className="text-2xl font-bold mb-4 text-gray-600">Email SMTP Settings</h3>
      <div className="space-y-4">
        {Object.keys(smtpConfig).map((key) => (
          <div key={key}>
            <label className="block text-gray-600 mb-1 capitalize">
              {key.replace(/([A-Z])/g, ' $1').trim()} <span className="text-red-500">*</span>
            </label>
            <input
              type={key === 'password' ? 'password' : key === 'port' ? 'number' : 'text'}
              value={smtpConfig[key]}
              onChange={(e) => setSmtpConfig({ ...smtpConfig, [key]: e.target.value })}
              className="w-full p-2 border rounded-lg border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder={`Enter ${key.replace(/([A-Z])/g, ' $1').toLowerCase()}`}
              required
            />
          </div>
        ))}
        <div className="flex justify-end space-x-4">
          <button
            onClick={handleTestEmail}
            disabled={!smtpFormValid || isTesting || isSaving}
            className={`px-6 py-2 rounded-lg font-semibold transition-colors duration-300 ${
              smtpFormValid && !isTesting && !isSaving
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-400 text-gray-700 cursor-not-allowed'
            }`}
          >
            {isTesting ? 'Testing...' : 'Test Email'}
          </button>
          <button
            onClick={handleSaveSmtpSettings}
            disabled={!smtpFormValid || isTesting || isSaving}
            className={`px-6 py-2 rounded-lg font-semibold transition-colors duration-300 ${
              smtpFormValid && !isTesting && !isSaving
                ? 'bg-purple-600 text-white hover:bg-purple-700'
                : 'bg-gray-400 text-gray-700 cursor-not-allowed'
            }`}
          >
            {isSaving ? 'Saving...' : 'Save SMTP Settings'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SmtpConfig;